class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton?.addEventListener('click', this.clear.bind(this));
    this.input?.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input?.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.form = this.querySelector('form');
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        if (this.dataset.predictiveSearchEnabled !== 'true') {
          e.preventDefault();
          this.input.focus();
        }
      });
    }

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    if (this.dataset.predictiveSearchEnabled !== 'true') return;

    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');

    // Initialize swipe arrows for static/cached recommendations
    this.initSwipeArrows();

    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.location.origin}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('resources[options][unavailable_products]', 'show'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.dataset.predictiveSearchEnabled !== 'true') return;

    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        // Apply highlight
        this.highlightMatch();

        // Initialize swipe arrows on dynamically injected markup
        this.initSwipeArrows();

        document.dispatchEvent(new CustomEvent('collection:rerendered'));

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = !this.contains(target);

    if (shouldClose) {
      this.input.blur();
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton?.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton?.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }

  highlightMatch() {
    const searchTerm = this.input.value.trim();
    if (!searchTerm) return;

    // Select the elements that should be highlighted.
    // The snippet uses wim-product-card__title and reversed-link for text suggestions
    const highlightElements = this.querySelectorAll('.predictive-search-result__list-item a, .wim-product-card__title a, .wim-product-card__title .reversed-link');

    if (highlightElements.length === 0) return;

    // Escape regex characters from search term
    const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Split search term by spaces to highlight individual words, ignoring short words like 'y', 'de'
    const words = searchTerm.split(/\s+/).filter(word => word.length > 2);
    if (words.length === 0) words.push(searchTerm); // fallback to full string if only short words

    const regexPattern = words.map(escapeRegExp).join('|');
    const regex = new RegExp(`(${regexPattern})`, 'gi');

    highlightElements.forEach(el => {
      // Use TreeWalker to only replace text nodes, avoiding messing up HTML
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
      const textNodes = [];
      let node;
      while ((node = walker.nextNode())) {
        textNodes.push(node);
      }

      textNodes.forEach(textNode => {
        const text = textNode.nodeValue;
        if (regex.test(text)) {
          const span = document.createElement('span');
          span.innerHTML = text.replace(regex, '<mark class="wim-predictive-search__highlight-match">$1</mark>');
          textNode.parentNode.replaceChild(span, textNode);
        }
      });
    });
  }

  initSwipeArrows() {
    const wrappers = this.querySelectorAll('.wim-swipe-mobile-wrapper');
    wrappers.forEach(wrapper => {
      if (wrapper.dataset.swipeInitialized === 'true') return;
      wrapper.dataset.swipeInitialized = 'true';

      const inner = wrapper.querySelector('.swipe-mobile');
      const prevBtn = wrapper.querySelector('.wim-swipe-arrow.prev');
      const nextBtn = wrapper.querySelector('.wim-swipe-arrow.next');
      if (!inner || !prevBtn || !nextBtn) return;

      const updateArrows = () => {
        const scrollLeft = inner.scrollLeft;
        const maxScrollLeft = inner.scrollWidth - inner.clientWidth;
        
        if (scrollLeft <= 5) {
          prevBtn.style.display = 'none';
        } else {
          prevBtn.style.display = 'flex';
        }
        
        if (scrollLeft >= maxScrollLeft - 5) {
          nextBtn.style.display = 'none';
        } else {
          nextBtn.style.display = 'flex';
        }
      };

      prevBtn.addEventListener('click', () => {
        inner.scrollBy({ left: -240, behavior: 'smooth' });
      });

      nextBtn.addEventListener('click', () => {
        inner.scrollBy({ left: 240, behavior: 'smooth' });
      });

      inner.addEventListener('scroll', updateArrows);
      setTimeout(updateArrows, 150);
    });
  }
}
customElements.define('predictive-search', PredictiveSearch);
